import { useTranslation } from 'react-i18next'
import { MuiOtpInput } from 'mui-one-time-password-input'
import { FormEvent, useState, forwardRef, useImperativeHandle } from 'react'
import { TextField, Button, InputAdornment, IconButton } from '@mui/material'

import { FormError, NewPassword } from '../../types'
import FaEye from '../../assets/icons/eye-showed-icon.svg'
import FaEyeSlash from '../../assets/icons/eye-slash-icon.svg'
import { textFieldWhiteBg, baseButtonWhiteBg } from '../../theme/custom-styles'

const NewPasswordForm = forwardRef(
  (
    {
      errors,
      showOtpModule,
      showActionsButton,
      handleNewPasswordFormSubmit
    }: {
      errors: FormError
      showOtpModule: boolean
      showActionsButton: boolean
      handleNewPasswordFormSubmit?: (event: FormEvent<HTMLFormElement>, newPasswordData: NewPassword) => Promise<void>
    },
    ref
  ) => {
    const { t } = useTranslation(['forgot-password'])

    const [otpCode, setOtpCode] = useState('')
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [passwordVisible, setPasswordVisible] = useState(false)
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false)

    useImperativeHandle(
      ref,
      () => {
        return {
          otpCode,
          password,
          confirmPassword
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [otpCode, password, confirmPassword]
    )

    return (
      <form
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          if (handleNewPasswordFormSubmit) handleNewPasswordFormSubmit(event, { password, confirmPassword })
        }}
      >
        <div className="forgot-password-form-new-password">
          <div className="forgot-password-form-new-password-field">
            <TextField
              fullWidth
              value={password}
              variant="standard"
              sx={textFieldWhiteBg}
              label={`${t('newPasswordForm.fields.newPassword')}`}
              error={!!errors.password}
              helperText={errors.password}
              type={passwordVisible ? 'text' : 'password'}
              onChange={(event) => setPassword(event.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      aria-label="toggle password visibility"
                      sx={{ color: '#107569', right: '10px', bottom: '3px' }}
                      onClick={() => setPasswordVisible((visible) => !visible)}
                    >
                      {passwordVisible ? (
                        <img
                          src={FaEyeSlash}
                          alt="eye-slash-icon"
                          style={{ width: '28px', height: '28px' }}
                        />
                      ) : (
                        <img
                          src={FaEye}
                          alt="eye-icon"
                          style={{ width: '28px', height: '28px' }}
                        />
                      )}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <div className="forgot-password-form-new-password-warning">{t('newPasswordForm.passwordInstruction')}</div>
          </div>
          <TextField
            fullWidth
            variant="standard"
            sx={textFieldWhiteBg}
            value={confirmPassword}
            error={!!errors.confirmPassword}
            label={`${t('newPasswordForm.fields.confirmNewPassword')}`}
            helperText={errors.confirmPassword}
            type={confirmPasswordVisible ? 'text' : 'password'}
            onChange={(event) => setConfirmPassword(event.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    aria-label="toggle password visibility"
                    sx={{ color: '#107569', right: '10px', bottom: '3px' }}
                    onClick={() => setConfirmPasswordVisible((visible) => !visible)}
                  >
                    {confirmPasswordVisible ? (
                      <img
                        src={FaEyeSlash}
                        alt="eye-slash-icon"
                        style={{ width: '28px', height: '28px' }}
                      />
                    ) : (
                      <img
                        src={FaEye}
                        alt="eye-icon"
                        style={{ width: '28px', height: '28px' }}
                      />
                    )}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          {showOtpModule && (
            <div
              className="step"
              style={{ marginTop: '0px', gap: '16px' }}
            >
              <div
                className="step-instruction-container"
                style={{ fontSize: '14px', color: '#F59E0B' }}
              >
                {t('checkCodeInstruction', { ns: 'two-factor-auth' })}
              </div>
              <MuiOtpInput
                length={6}
                value={otpCode}
                onChange={(newValue: string) => setOtpCode(newValue)}
                className={errors?.otpCodeError ? 'MuiOtpInput-Box-inner MuiOtpInput-Box-error' : 'MuiOtpInput-Box-inner'}
              />
            </div>
          )}
        </div>
        {errors.newPasswordGeneralError && <div className="error-message">{errors.newPasswordGeneralError}</div>}
        {showActionsButton && (
          <div className="forgot-password-action-button">
            <Button
              type="submit"
              variant="contained"
              sx={baseButtonWhiteBg}
            >
              {t('newPasswordForm.buttonText')}
            </Button>
          </div>
        )}
      </form>
    )
  }
)

export default NewPasswordForm
